import { TextField } from "@mui/material";
import { NumberFormatBase } from "react-number-format";
import { hhmmToDecimal } from "shared/helpers/horasHHMM";

// ============================================
// INTERFACES
// ============================================

interface InputHoraHHMMProps {
  /** Valor no formato HH:MM (ex: "01:30") */
  value: string;
  /** Callback com o valor convertido para decimal (ex: 1.5) e o texto HH:MM exibido */
  onChangeDecimal: (decimal: number | undefined, hhmm: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
}

// ============================================
// FORMATAÇÃO
// ============================================

/**
 * Formata uma sequência de dígitos como HH:MM, inserindo o ":" automaticamente.
 * Os 2 últimos dígitos são sempre os minutos (limitados a 59) e os demais as
 * horas, suportando horas com 3+ dígitos.
 * Ex: "0130" -> "01:30", "5025" -> "50:25", "15035" -> "150:35".
 */
const formatHHMM = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 6);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;

  const horas = digits.slice(0, digits.length - 2);
  let minutos = digits.slice(-2);
  if (parseInt(minutos, 10) > 59) {
    minutos = "59";
  }
  return `${horas.padStart(2, "0")}:${minutos}`;
};

/** Remove a formatação, mantendo apenas os dígitos. */
const removeFormatHHMM = (value: string): string =>
  value.replace(/\D/g, "").slice(0, 6);

// ============================================
// COMPONENTE
// ============================================

/**
 * Campo de hora no formato HH:MM com máscara automática durante a digitação.
 * O ":" é inserido automaticamente (digitar "0130" exibe "01:30") e os
 * minutos são limitados a 59. O valor é propagado como decimal (1.5 = 01:30).
 */
export const InputHoraHHMM = ({
  value,
  onChangeDecimal,
  disabled = false,
  label,
  placeholder = "HH:MM",
  size = "small",
  fullWidth = true,
}: InputHoraHHMMProps) => {
  return (
    <NumberFormatBase
      customInput={TextField}
      format={formatHHMM}
      removeFormatting={removeFormatHHMM}
      value={value}
      onValueChange={(values, sourceInfo) => {
        // Propaga apenas em interação real do usuário. O react-number-format
        // também dispara onValueChange quando o valor chega por prop (montagem/
        // re-render); nesse caso `source === "prop"` e propagar reintroduziria o
        // ruído da conversão lossy decimal↔HH:MM, marcando o formulário como
        // alterado sem o usuário ter tocado no campo.
        if (sourceInfo.source !== "event") return;
        const hhmm = values.formattedValue || "";
        onChangeDecimal(hhmmToDecimal(hhmm), hhmm);
      }}
      disabled={disabled}
      label={label}
      placeholder={placeholder}
      size={size}
      fullWidth={fullWidth}
      inputProps={{
        maxLength: 7,
        style: { fontFamily: "monospace" },
        inputMode: "numeric",
      }}
    />
  );
};
